import { authFetch } from './authFetch';

export interface UserRecord {
  id: string;
  email: string;
  name: string;
  role: string;
  role_display_name: string;
  organization_name: string;
  is_kpmg: boolean;
  created_at: string;
}

export interface RoleRecord {
  id: number;
  name: string;
  display_name: string;
  description: string;
  is_domain_scoped: boolean;
  user_count: number;
}

export interface AssetAccessRule {
  id?: number;
  assessment_type: string;
  asset_class: string;
  client_visible: boolean;
  enabled_by?: string;
  enabled_at?: string;
}

export interface AssessmentSubDomain {
  name: string;
  domain_id: string;
  assessment_id: string;
  is_custom?: boolean;
}

export interface AssessmentCategory {
  name: string;
  sub_domains: AssessmentSubDomain[];
}

export interface ResourceRequestRecord {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  assessment_type: string;
  asset_class: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by?: string;
  reviewed_at?: string;
  created_at: string;
}

export interface AdminNotificationRecord {
  id: number;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export interface DashboardStats {
  users: { total: number; kpmg_staff: number; clients: number };
  roles: { total: number; breakdown: { name: string; display_name: string; user_count: number }[] };
  organizations: { total: number };
  assessments: { total: number; by_category: { category: string; count: number }[] };
  asset_access: { total_rules: number; enabled_for_clients: number };
}

// ── Users ───────────────────────────────────────────────────

export async function fetchUsers(): Promise<UserRecord[]> {
  const res = await authFetch('/api/admin/users');
  if (!res.ok) throw new Error(`Failed to fetch users: ${res.status}`);
  const data = await res.json();
  return data.users;
}

export async function updateUserRole(
  userId: string,
  role: string,
): Promise<void> {
  const res = await authFetch(`/api/admin/users/${userId}/role`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update role: ${res.status}`);
  }
}

// ── Roles ───────────────────────────────────────────────────

export async function fetchRoles(): Promise<RoleRecord[]> {
  const res = await authFetch('/api/admin/roles');
  if (!res.ok) throw new Error(`Failed to fetch roles: ${res.status}`);
  const data = await res.json();
  return data.roles;
}

export async function createRole(
  role: { name: string; display_name: string; description: string; is_domain_scoped: boolean },
): Promise<void> {
  const res = await authFetch('/api/admin/roles', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(role),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create role: ${res.status}`);
  }
}

export async function deleteRole(roleName: string): Promise<void> {
  const res = await authFetch(`/api/admin/roles/${roleName}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to delete role: ${res.status}`);
  }
}

// ── Asset Access Rules ──────────────────────────────────────

export async function fetchAssetAccessRules(): Promise<AssetAccessRule[]> {
  const res = await authFetch('/api/admin/asset-access');
  if (!res.ok) throw new Error(`Failed to fetch access rules: ${res.status}`);
  const data = await res.json();
  return data.rules;
}

export async function updateAssetAccessRule(
  rule: { assessment_type: string; asset_class: string; client_visible: boolean },
): Promise<void> {
  const res = await authFetch('/api/admin/asset-access', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rule),
  });
  if (!res.ok) throw new Error(`Failed to update access rule: ${res.status}`);
}

// ── Dashboard Stats ─────────────────────────────────────────

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const res = await authFetch('/api/admin/stats');
  if (!res.ok) throw new Error(`Failed to fetch dashboard stats: ${res.status}`);
  const data = await res.json();
  return data.stats;
}

// ── Current User ────────────────────────────────────────────

export async function fetchCurrentUser(): Promise<{
  role: string;
  is_admin: boolean;
  id: string;
  email: string;
  name: string;
}> {
  const res = await authFetch('/api/auth/me');
  if (!res.ok) throw new Error(`Failed to fetch user profile: ${res.status}`);
  const data = await res.json();
  return data.user;
}

// ── Assessment Domains ──────────────────────────────────────

export async function fetchAssessmentDomains(): Promise<AssessmentCategory[]> {
  const res = await authFetch('/api/admin/assessment-domains');
  if (!res.ok) throw new Error(`Failed to fetch assessment domains: ${res.status}`);
  const data = await res.json();
  return data.categories;
}

// ── Resource Requests ───────────────────────────────────────

export async function fetchResourceRequests(status?: string): Promise<ResourceRequestRecord[]> {
  const url = status ? `/api/admin/resource-requests?status=${status}` : '/api/admin/resource-requests';
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`Failed to fetch resource requests: ${res.status}`);
  const data = await res.json();
  return data.requests;
}

export async function reviewResourceRequest(
  requestId: string,
  action: 'approve' | 'reject',
): Promise<void> {
  const res = await authFetch(`/api/admin/resource-requests/${requestId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to review request: ${res.status}`);
  }
}

// ── Admin Notifications ─────────────────────────────────────

export async function fetchAdminNotifications(): Promise<{
  pending_requests: number;
  notifications: AdminNotificationRecord[];
}> {
  const res = await authFetch('/api/admin/notifications');
  if (!res.ok) throw new Error(`Failed to fetch notifications: ${res.status}`);
  const data = await res.json();
  return { pending_requests: data.pending_requests, notifications: data.notifications };
}

export async function markNotificationRead(notifId: number): Promise<void> {
  const res = await authFetch(`/api/admin/notifications/${notifId}/read`, { method: 'PUT' });
  if (!res.ok) throw new Error(`Failed to mark notification read: ${res.status}`);
}

// ── Domain Assignments ──────────────────────────────────────

export async function fetchUserDomains(userId: string): Promise<string[]> {
  const res = await authFetch(`/api/admin/users/${userId}/domains`);
  if (!res.ok) throw new Error(`Failed to fetch user domains: ${res.status}`);
  const data = await res.json();
  return data.domains;
}

export async function updateUserDomains(
  userId: string,
  domains: string[],
): Promise<void> {
  const res = await authFetch(`/api/admin/users/${userId}/domains`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domains }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update user domains: ${res.status}`);
  }
}
